import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { Button, message } from "antd";
import axios from "axios";
import classNames from "classnames";
import {
  AudioChangeAction,
  MutedSource,
  VideoCapturingState,
} from "@zoom/videosdk";

import { useZoom } from "../zoom/ZoomProvider";
import { loadDevicePrefs, saveDevicePrefs } from "../zoom/devicePrefs";
import { RoomCacheContext } from "../../context/roomContext";
import type { RoomContext } from "../../context/types";
import "../styles/videoFooter.scss";

import MicControl from "./controls/MicControl";
import CameraControl from "./controls/CameraControl";
import LeaveButton from "./controls/LeaveButton";
import RemoteViewControl from "./controls/RemoteViewControl";

function errToText(e: any) {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e.reason) return String(e.reason);
  if (e.message) return String(e.message);
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

export default function VideoFooter({ className }: { className?: string }) {
  const { client, mediaStream, leave, status } = useZoom();
  const { roomCache } = useContext(RoomCacheContext) as RoomContext;

  const prefs = useMemo(() => loadDevicePrefs(), []);

  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isStartedAudio, setIsStartedAudio] = useState<boolean>(false);
  const [isStartedVideo, setIsStartedVideo] = useState<boolean>(false);

  type Device = { label: string; deviceId: string };

    const [micList, setMicList] = useState<Device[]>([]);
    const [speakerList, setSpeakerList] = useState<Device[]>([]);
    const [cameraList, setCameraList] = useState<Device[]>([]);

    const [activeMicId, setActiveMicId] = useState<string | undefined>(undefined);
    const [activeSpeakerId, setActiveSpeakerId] = useState<string | undefined>(undefined);
    const [activeCameraId, setActiveCameraId] = useState<string | undefined>(undefined);

    const [isMirrored, setIsMirrored] = useState(false);
    const [isBlur, setIsBlur] = useState(false);
    const [remoteFit, setRemoteFit] = useState(loadDevicePrefs().remoteFitMode ?? "contain");

    useEffect(() => {
      const onStorage = (e: StorageEvent) => {
        if (e.key === "telehealth_device_prefs_v1") {
          setRemoteFit(loadDevicePrefs().remoteFitMode ?? "contain");
        }
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    }, []);

  // For debugging / safety
  const joined = status === "joined";

  // Keep UI in sync with current user info (safe guards)
  const syncFromCurrentUser = useCallback(() => {
    if (!client?.getCurrentUserInfo) return;
    const u = client.getCurrentUserInfo();
    if (!u) return;
    setIsMuted(!!u.muted);
    setIsStartedVideo(!!u.bVideoOn);
    setIsStartedAudio(!!u.audio); // audio is "" when not joined to audio
  }, [client]);

  useEffect(() => {
    if (!joined || !mediaStream) return;

    const syncDevices = () => {
        try {
        setMicList(mediaStream.getMicList?.() || []);
        setSpeakerList(mediaStream.getSpeakerList?.() || []);
        setCameraList(mediaStream.getCameraList?.() || []);
        setActiveMicId(mediaStream.getActiveMicrophone?.());
        setActiveSpeakerId(mediaStream.getActiveSpeaker?.());
        setActiveCameraId(mediaStream.getActiveCamera?.());
        } catch (e) {
        console.warn("syncDevices failed", e);
        }
    };

    syncDevices();
    client?.on?.("device-change", syncDevices);

    return () => {
        client?.off?.("device-change", syncDevices);
    };
}, [joined, mediaStream, client]);

  // Subscribe to zoom events so UI stays correct
  useEffect(() => {
    if (!client) return;

    const onUserUpdated = () => syncFromCurrentUser();

    const onCurrentAudioChange = (payload: any) => {
      const { action, source } = payload || {};
      if (action === AudioChangeAction.Join) {
        setIsStartedAudio(true);
        setTimeout(syncFromCurrentUser, 250);
      } else if (action === AudioChangeAction.Leave) {
        setIsStartedAudio(false);
      } else if (action === AudioChangeAction.Muted) {
        setIsMuted(true);
        if (source === MutedSource.PassiveByMuteOne) message.info("Host muted you");
      } else if (action === AudioChangeAction.Unmuted) {
        setIsMuted(false);
        if (source === "passive") message.info("Host unmuted you");
      }
    };

    const onVideoCapturingChange = (payload: any) => {
      if (!payload) return;
      setIsStartedVideo(payload.state === VideoCapturingState.Started);
    };

    client.on("user-updated", onUserUpdated);
    client.on("current-audio-change", onCurrentAudioChange);
    client.on("video-capturing-change", onVideoCapturingChange);

    // Initial sync
    syncFromCurrentUser();

    return () => {
      client.off("user-updated", onUserUpdated);
      client.off("current-audio-change", onCurrentAudioChange);
      client.off("video-capturing-change", onVideoCapturingChange);
    };
  }, [client, syncFromCurrentUser]);

  const toggleMute = useCallback(async () => {
  if (!mediaStream || !client?.getCurrentUserInfo) return;

  try {
    const currentUser = client.getCurrentUserInfo();
    const hasAudio = !!currentUser?.audio;

    // If Zoom audio has not started yet, this click should mean:
    // "join audio and unmute me" — not just "prepare audio".
    if (!hasAudio) {
      await mediaStream.startAudio({ highBitrate: true });
      await mediaStream.unmuteAudio();

      setIsStartedAudio(true);
      setIsMuted(false);
      saveDevicePrefs({ micMuted: false });

      setTimeout(syncFromCurrentUser, 250);
      return;
    }

    const currentlyMuted = !!client.getCurrentUserInfo()?.muted;

    if (currentlyMuted) {
      await mediaStream.unmuteAudio();
      setIsMuted(false);
      saveDevicePrefs({ micMuted: false });
    } else {
      await mediaStream.muteAudio();
      setIsMuted(true);
      saveDevicePrefs({ micMuted: true });
    }

    setTimeout(syncFromCurrentUser, 250);
  } catch (e) {
    message.error(`Audio error: ${errToText(e)}`);
    console.warn(e);
    syncFromCurrentUser();
  }
}, [mediaStream, client, syncFromCurrentUser]);

  const toggleVideo = useCallback(async () => {
    if (!mediaStream || !client?.getCurrentUserInfo) return;

    try {
      const on = !!client.getCurrentUserInfo()?.bVideoOn;
      if (on) {
        await mediaStream.stopVideo();
        setIsStartedVideo(false);
        saveDevicePrefs({ videoOn: false });
      } else {
        await mediaStream.startVideo({ hd: true, fullHd: true });
        setIsStartedVideo(true);
        saveDevicePrefs({ videoOn: true });
      }
    } catch (e) {
      // This is where your prior code was crashing with [object Object]
      message.error(`Camera error: ${errToText(e)}`);
      console.warn(e);
      syncFromCurrentUser();
    }
  }, [mediaStream, client, syncFromCurrentUser]);

  const endConsult = useCallback(async () => {
    try {
      if (roomCache?.roomCacheId) {
        await axios.post("/api/endConsult", { roomCacheId: roomCache.roomCacheId });
      }
    } catch (e) {
      console.warn("endConsult api failed", e);
    } finally {
      try {
        await leave();
        window.location.assign("/consult-complete");
      } catch (error) {
        console.error("Failed to leave Zoom session", error);
        message.error(
          `Unable to leave the consultation: ${errToText(error)}. Please try again.`,
        );
      }
    }
  }, [leave, roomCache]);

  const onMicMenuSelect = useCallback(
  async (key: string) => {
    if (!mediaStream) return;
    const [type, deviceId] = key.split("|");
    try {
      if (type === "microphone") {
        await mediaStream.switchMicrophone(deviceId);
        setActiveMicId(mediaStream.getActiveMicrophone());
        saveDevicePrefs({ micId: deviceId });
      } else if (type === "speaker") {
        await mediaStream.switchSpeaker(deviceId);
        setActiveSpeakerId(mediaStream.getActiveSpeaker());
        saveDevicePrefs({ speakerId: deviceId });
      }
    } catch (e) {
      message.error(`Device error: ${errToText(e)}`);
    }
  },
  [mediaStream]
);

const onSelectCamera = useCallback(
  async (deviceId: string) => {
    if (!mediaStream) return;
    try {
      await mediaStream.switchCamera(deviceId);
      setActiveCameraId(mediaStream.getActiveCamera());
      saveDevicePrefs({ cameraId: deviceId });
    } catch (e) {
      message.error(`Camera switch error: ${errToText(e)}`);
    }
  },
  [mediaStream]
);

const toggleMirror = useCallback(async () => {
  if (!mediaStream) return;
  try {
    await mediaStream.mirrorVideo(!isMirrored);
    setIsMirrored((v) => !v);
  } catch (e) {
    message.error(`Mirror error: ${errToText(e)}`);
  }
}, [mediaStream, isMirrored]);

const toggleBlur = useCallback(async () => {
  if (!mediaStream) return;
  try {
    const supported = mediaStream.isSupportVirtualBackground?.();
    if (!supported) return;
    if (isBlur) {
      await mediaStream.updateVirtualBackgroundImage(undefined);
    } else {
      await mediaStream.updateVirtualBackgroundImage("blur");
    }
    setIsBlur((v) => !v);
  } catch (e) {
    message.error(`Blur error: ${errToText(e)}`);
  }
}, [mediaStream, isBlur]);


  return (
    <div className={classNames("video-footer", className)} style={{ display: "flex", gap: 12 }}>
        <MicControl
            disabled={!joined || !mediaStream}
            isStartedAudio={isStartedAudio}
            isMuted={isMuted}
            micList={micList}
            speakerList={speakerList}
            activeMicId={activeMicId}
            activeSpeakerId={activeSpeakerId}
            onToggle={toggleMute}
            onSelect={onMicMenuSelect}
        />

        <CameraControl
          disabled={!joined || !mediaStream}
          isStartedVideo={isStartedVideo}
          cameraList={cameraList}
          activeCameraId={activeCameraId}
          isMirrored={isMirrored}
          isBlur={isBlur}
          showMirror={true}
          showBlur={true}
          showRotateSelf={true}
          onToggle={toggleVideo}
          onSelectCamera={onSelectCamera}
          onToggleMirror={toggleMirror}
          onToggleBlur={toggleBlur}
        />
        <LeaveButton onLeaveClick={endConsult} />
    </div>
    );
}
